import { Select } from '@cortex/web-design-system/components/ui/Select';
import { formatLocalYmd } from '../../../lib/date-utils.js';
import {
  feedbackAtVersion,
  hasSkillName,
  judgmentFor,
  kindLabel,
  objectionsFor,
  skillMapLabel,
  skillMapTitle,
} from '../../../lib/project-feedback-view.js';
import type {
  FeedbackEntry,
  FeedbackScore,
  FeedbackUnassessable,
  ProjectFeedbackDetail,
  ProjectFeedbackRevision,
} from '../../../types/project-feedback.js';
import { FeedbackEntryItem } from './FeedbackEntryItem.js';

/** @graph-connects none */
export interface FeedbackDetailViewProps {
  /** API の詳細 */
  detail: ProjectFeedbackDetail;
  /** 全版（新しい順） */
  revisions: ProjectFeedbackRevision[];
  /** 表示する版（未指定は最新版） */
  version?: number;
  /** 版を切り替える */
  onVersionChange: (version: number) => void;
  /** 本人として見ているか（本人ならメンバー名を出さない） */
  isOwner: boolean;
  /** メールから表示名を引く */
  nameOf: (email: string) => string;
}

/**
 * good / more の列。more も good と同じ落ち着いた見た目にする（減点ではなく次の一手のため）。
 * @graph-connects FeedbackEntryItem [calls] 項目の表示
 * @graph-connects judgmentFor [calls] 項目の判断
 * @graph-connects objectionsFor [calls] 項目の異議
 */
function EntryColumn({
  title,
  tone,
  entries,
  detail,
  version,
  nameOf,
}: {
  title: string;
  tone: 'good' | 'more';
  entries: FeedbackEntry[];
  detail: ProjectFeedbackDetail;
  version: number;
  nameOf: (email: string) => string;
}) {
  const judgments = detail.judgments;
  return (
    <section className="min-w-0 space-y-3" aria-label={title}>
      <h2
        className={`text-sm font-semibold ${
          tone === 'good' ? 'text-[var(--color-success)]' : 'text-[var(--color-accent-primary)]'
        }`}
      >
        {title}
        <span className="ml-2 text-xs font-normal text-[var(--color-text-muted)]">
          {entries.length}
        </span>
      </h2>
      <ul className="space-y-3">
        {entries.map((entry) => (
          <FeedbackEntryItem
            key={entry.entryId}
            feedbackId={detail.feedback.feedbackId}
            version={version}
            entry={entry}
            judgment={judgmentFor(judgments, version, entry.entryId)}
            objections={objectionsFor(detail.objections, version, entry.entryId)}
            permissions={detail.permissions}
            verdictOptions={detail.verdictOptions}
            nameOf={nameOf}
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * criterion ごとの暫定スコア。
 * @graph-connects none
 */
function ScoreList({ scores }: { scores: FeedbackScore[] }) {
  return (
    <section aria-label="暫定スコア" className="space-y-2">
      <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">暫定スコア</h2>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {scores.map((score) => (
          <li
            key={score.skillMapId}
            className="flex items-baseline justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2"
          >
            <span
              title={skillMapTitle(score)}
              className={`truncate text-xs text-[var(--color-text-secondary)]${hasSkillName(score) ? '' : ' font-mono'}`}
            >
              {skillMapLabel(score)}
            </span>
            <span className="shrink-0">
              <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                {score.score === null ? '-' : score.score.toFixed(1)}
              </span>
              <span className="ml-1 text-[11px] text-[var(--color-text-muted)]">
                {score.evidenceResult}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 記録が無く判断できなかった観点（good / more とは分けて小さく出す）。
 * @graph-connects none
 */
function UnassessableList({ items }: { items: FeedbackUnassessable[] }) {
  return (
    <section aria-label="判断できなかった観点" className="space-y-1">
      <h2 className="text-xs font-semibold text-[var(--color-text-muted)]">判断できなかった観点</h2>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.skillMapId} className="text-xs text-[var(--color-text-muted)]">
            <span
              title={skillMapTitle(item)}
              className={hasSkillName(item) ? 'font-medium' : 'font-mono'}
            >
              {skillMapLabel(item)}
            </span>{' '}
            {item.neededRecord}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 案件 FB の詳細（版の切替・good と more・暫定スコア・判断できなかった観点）。
 * 何を出すか・何ができるかは API の返した中身と permissions だけで決める。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Show one project feedback version with good and more side by side, provisional criterion scores, unassessable aspects and a version switcher
 * @graph-connects feedbackAtVersion [calls] 表示する版の選択
 * @graph-connects EntryColumn [calls] good / more の列
 * @graph-connects ScoreList [calls] 暫定スコア
 * @graph-connects UnassessableList [calls] 判断できなかった観点
 */
export function FeedbackDetailView({
  detail,
  revisions,
  version,
  onVersionChange,
  isOwner,
  nameOf,
}: FeedbackDetailViewProps) {
  const feedback = feedbackAtVersion(detail.feedback, revisions, version);
  const versionOptions =
    revisions.length > 0
      ? revisions.map((r) => ({
          value: String(r.version),
          label: `v${String(r.version)} ${formatLocalYmd(r.savedAt)}`,
        }))
      : [{ value: String(feedback.version), label: `v${String(feedback.version)}` }];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] break-words">
            {feedback.projectTitle}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-[var(--color-text-secondary)]">
            <a
              href={`https://air-closet.backlog.jp/view/${feedback.pjKey}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs text-[var(--color-accent-primary)] hover:underline"
            >
              {feedback.pjKey}
            </a>
            <span>{kindLabel(feedback.kind)}</span>
            {!isOwner && <span>{feedback.nickname}</span>}
            <span className="text-xs text-[var(--color-text-muted)]">
              {formatLocalYmd(feedback.generatedAt)}
            </span>
          </p>
        </div>
        {versionOptions.length > 1 && (
          <Select
            options={versionOptions}
            value={String(feedback.version)}
            onChange={(event) => {
              onVersionChange(Number(event.target.value));
            }}
            aria-label="版"
            size="sm"
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <EntryColumn
          title="Good"
          tone="good"
          entries={feedback.good}
          detail={detail}
          version={feedback.version}
          nameOf={nameOf}
        />
        <EntryColumn
          title="More"
          tone="more"
          entries={feedback.more}
          detail={detail}
          version={feedback.version}
          nameOf={nameOf}
        />
      </div>

      {feedback.scores !== undefined && feedback.scores.length > 0 && (
        <ScoreList scores={feedback.scores} />
      )}
      {feedback.unassessable.length > 0 && <UnassessableList items={feedback.unassessable} />}
    </div>
  );
}
